import { Component, OnInit } from '@angular/core';
import { OrganizationService } from 'src/app/services/merchant/organization.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import Swal from 'sweetalert2';

/**
 * La ficha de la organizacion: CrediTotal.
 *
 * 📍 No es un listado y no sigue el molde de shops-list: hay UN registro. Es una
 * ficha de configuracion.
 *
 * ⚠️ Tres de los cinco campos estan vacios en la base (razon social, RIF y
 * domicilio). La pantalla los muestra como PENDIENTES —"Sin registrar"— y no
 * como campos en blanco: son informacion, no un error. Sin datos fiscales no se
 * emite documentacion a nombre de CrediTotal.
 *
 * 🎨 Diseño: la pantalla arranca en modo LECTURA (pares dato/valor .kb-datos) y
 * pasa a modo EDICION con el boton del encabezado. Asi una ficha con tres
 * campos vacios no se lee como un formulario abandonado.
 *
 * 🔴 REDISEÑO 2026-09-20 (aprobado por Juan: «no quiero esas cajas grandes en el
 * nuevo diseño»). Ocho cajas para cinco campos pasaron a dos secciones planas y
 * una cinta. Se fueron la barra de completitud, las tres metricas, la tarjeta
 * «Donde encaja» y el aviso repetido — y con ellos la llamada que pedia todos
 * los comercios sin paginar. Cada corte esta justificado donde estaba el codigo.
 */
@Component({
  selector: 'app-organizacion',
  templateUrl: './organizacion.component.html',
  styleUrls: ['./organizacion.component.css']
})
export class OrganizacionComponent implements OnInit {

  loading = true;
  guardando = false;
  noExiste = false;

  /** Modo edicion. En false la ficha se lee; en true se escribe. */
  editando = false;

  // Los campos fiscales que el back reporta vacios
  faltantes: string[] = [];

  // 🔴 REDISEÑO 2026-09-20 — las metricas se fueron, y con ellas la llamada que
  // las alimentaba. `cargarMetricas()` pedia TODOS los comercios sin paginar
  // (`getMerchants({})`) para contar tres numeros —cuantos hay, cuantas tiendas
  // suman, cuantos estan suspendidos— que ya se muestran en la pantalla de
  // Comercios, y que esta pantalla ademas repetia DOS VECES cada uno: arriba en
  // las tarjetas y otra vez abajo en «Donde encaja».
  //
  // Con la red creciendo, esa llamada es el patron que Juan marco el 2026-09-19
  // («si tienes consultas que llamen 20 veces en una vista, eso no lo estas
  // haciendo bien»). Y no pagaba nada: la decision de esta pantalla es cargar
  // tres campos fiscales. Si hace falta ver la red, hay un enlace al pie.

  // Los tres que hacen falta para emitir documentacion
  readonly CAMPOS_FISCALES = ['legal_name', 'tax_id', 'address'];

  formData = {
    code: '',
    name: '',
    legal_name: '',
    tax_id: '',
    address: ''
  };

  // Copia de lo cargado, para saber si hay cambios sin guardar
  private original = '';

  readonly etiquetas: any = {
    legal_name: 'Razón social',
    tax_id: 'RIF',
    address: 'Domicilio fiscal'
  };

  constructor(
    private _organization: OrganizationService,
    public _auth: AuthService
  ) { }

  ngOnInit(): void { this.cargar(); }

  /**
   * El valor de un campo por su nombre. Existe para que la plantilla recorra
   * `CAMPOS_FISCALES` sin indexar el objeto —`formData[campo]` no pasa la
   * comprobacion de tipos de la plantilla— y para no escribir los tres campos
   * tres veces en el HTML.
   */
  valor = (campo: string): string => (this.formData as any)[campo] || '';

  // 🔴 `completos` y `porcentaje` se fueron con la barra «Ficha completa 40%».
  //
  // El denominador eran los CINCO campos, y dos de ellos —`code` y `name`— no
  // pueden estar vacios: el codigo es fijo y el nombre nunca viene en blanco.
  // O sea que la barra NO PODIA BAJAR DE 40% aunque faltaran los tres datos
  // fiscales, que es justo lo que esta pantalla existe para avisar. Un numero
  // que no puede dar mal no informa: decora.
  //
  // Lo que informa es `pendientes`, que cuenta solo los tres que importan y
  // ahora es la cinta de arriba.

  /**
   * Los pendientes fiscales que se MUESTRAN. Se derivan de formData y no del
   * `faltantes` que manda el back a proposito: asi el aviso y la metrica se
   * actualizan mientras se escribe, sin esperar el guardado. Al recargar los
   * dos coinciden porque son los mismos tres campos.
   */
  get pendientes(): string[] {
    return this.CAMPOS_FISCALES.filter(c => !(this.formData as any)[c]);
  }

  get hayCambios(): boolean {
    return JSON.stringify(this.formData) !== this.original;
  }

  get puedeEditar(): boolean {
    return this._auth.hasPermission('config.organizacion.update');
  }

  /** Pasa a modo edicion. Sin permiso no hace nada. */
  entrarEnEdicion = (): void => {
    if (!this.puedeEditar) { return; }
    this.editando = true;
  };

  /** Sale de edicion tirando los cambios: vuelve a pedir la ficha al back. */
  cancelarEdicion = (): void => {
    this.editando = false;
    this.descartar();
  };

  cargar = async (): Promise<void> => {
    this.loading = true;
    try {
      const rep = await this._organization.getOrganization();
      const o = rep?.organization || {};
      this.formData = {
        code: o.code || '',
        name: o.name || '',
        legal_name: o.legal_name || '',
        tax_id: o.tax_id || '',
        address: o.address || ''
      };
      this.faltantes = rep?.faltantes || [];
      this.original = JSON.stringify(this.formData);
      this.noExiste = false;
    } catch (error: any) {
      if (error?.status === 404) {
        this.noExiste = true;
      } else {
        Swal.fire({
          icon: 'error',
          title: 'No se pudo cargar la organización',
          text: error?.error?.msg || 'Intenta de nuevo en un momento'
        });
      }
    } finally {
      this.loading = false;
    }
  };

  /** Normaliza el RIF a J-00000000-0 sin depender de una librería de máscara. */
  normalizarRif = (): void => {
    const crudo = (this.formData.tax_id || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!crudo) { this.formData.tax_id = ''; return; }
    const letra = /^[JGVEP]/.test(crudo) ? crudo[0] : 'J';
    const digitos = crudo.replace(/^[A-Z]/, '');
    this.formData.tax_id = digitos.length > 1
      ? `${letra}-${digitos.slice(0, -1)}-${digitos.slice(-1)}`
      : `${letra}-${digitos}`;
  };

  guardar = async (): Promise<void> => {
    if (!this.formData.name?.trim()) {
      Swal.fire({ icon: 'warning', title: 'El nombre no puede quedar vacío' });
      return;
    }
    this.guardando = true;
    try {
      // El `code` no se manda: el back lo rechaza porque lo referencian los datos.
      const { code, ...cambios } = this.formData;
      await this._organization.updateOrganization(cambios);
      this.original = JSON.stringify(this.formData);
      await this.cargar();
      this.editando = false;
      Swal.fire({ icon: 'success', title: 'Organización actualizada', timer: 1600, showConfirmButton: false });
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo guardar',
        text: error?.error?.msg || 'Intenta de nuevo en un momento'
      });
    } finally {
      this.guardando = false;
    }
  };

  descartar = (): void => { this.cargar(); };
}
